/**
 * Color utility functions for managing colors in the application
 */

/**
 * Converts a hex color to RGB values
 * @param {string} hexColor - Hex color string (with or without #)
 * @returns {Object} RGB object with r, g, b properties
 */
export const hexToRgb = (hexColor) => {
    if (!hexColor) return null;

    // Remove # if present
    const color = hexColor.replace('#', '');

    // Validate hex color format
    if (!/^[0-9A-F]{6}$/i.test(color)) {
        console.warn(`Invalid hex color format: ${hexColor}`);
        return null;
    }

    return {
        r: parseInt(color.substr(0, 2), 16),
        g: parseInt(color.substr(2, 2), 16),
        b: parseInt(color.substr(4, 2), 16)
    };
};

/**
 * Converts RGB values to hex color string
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {string} Hex color string with #
 */
export const rgbToHex = (r, g, b) => {
    const toHex = (n) => {
        const hex = Math.round(Math.max(0, Math.min(255, n))).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Calculates the luminance of a color
 * @param {string} hexColor - Hex color string
 * @returns {number} Luminance value between 0 and 1
 */
export const getLuminance = (hexColor) => {
    const rgb = hexToRgb(hexColor);
    if (!rgb) return 0;

    const { r, g, b } = rgb;

    // Calculate relative luminance using the formula from WCAG
    // https://www.w3.org/WAI/GL/wiki/Relative_luminance
    const getRGB = (c) => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };

    return 0.2126 * getRGB(r) + 0.7152 * getRGB(g) + 0.0722 * getRGB(b);
};

/**
 * Determines the best text color (black or white) for a given background color
 * @param {string} hexColor - Background hex color
 * @param {Object} options - Options for customization
 * @param {string} options.lightColor - Color to return for light backgrounds (default: '#000000')
 * @param {string} options.darkColor - Color to return for dark backgrounds (default: '#ffffff')
 * @param {number} options.threshold - Luminance threshold (default: 0.5)
 * @returns {string} Text color (hex)
 */
export const getTextColor = (hexColor, options = {}) => {
    const {
        lightColor = '#000000',
        darkColor = '#ffffff',
        threshold = 0.5
    } = options;

    if (!hexColor) return darkColor;

    const luminance = getLuminance(hexColor);
    return luminance > threshold ? lightColor : darkColor;
};

/**
 * Lightens a color by a given percentage
 * @param {string} hexColor - Hex color string
 * @param {number} percent - Percentage to lighten (0-100)
 * @returns {string} Lightened hex color
 */
export const lightenColor = (hexColor, percent = 20) => {
    const rgb = hexToRgb(hexColor);
    if (!rgb) return hexColor;

    const { r, g, b } = rgb;

    const lighten = (c) => Math.min(255, c + (255 - c) * (percent / 100));

    return rgbToHex(
        Math.round(lighten(r)),
        Math.round(lighten(g)),
        Math.round(lighten(b))
    );
};

/**
 * Darkens a color by a given percentage
 * @param {string} hexColor - Hex color string
 * @param {number} percent - Percentage to darken (0-100)
 * @returns {string} Darkened hex color
 */
export const darkenColor = (hexColor, percent = 20) => {
    const rgb = hexToRgb(hexColor);
    if (!rgb) return hexColor;

    const { r, g, b } = rgb;

    const darken = (c) => Math.max(0, c - c * (percent / 100));

    return rgbToHex(
        Math.round(darken(r)),
        Math.round(darken(g)),
        Math.round(darken(b))
    );
};

/**
 * Adjusts the opacity of a hex color by converting to rgba
 * @param {string} hexColor - Hex color string
 * @param {number} opacity - Opacity value (0-1)
 * @returns {string} RGBA color string
 */
export const addOpacity = (hexColor, opacity = 1) => {
    const rgb = hexToRgb(hexColor);
    if (!rgb) return `rgba(0, 0, 0, ${opacity})`;

    const { r, g, b } = rgb;
    return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, opacity))})`;
};

/**
 * Generates a random hex color
 * @returns {string} Random hex color
 */
export const getRandomColor = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
};

/**
 * Checks if a string is a valid hex color
 * @param {string} color - Color string to validate
 * @returns {boolean} True if valid hex color
 */
export const isValidHexColor = (color) => {
    if (!color || typeof color !== 'string') return false;

    // Remove # if present
    const hex = color.replace('#', '');

    // Check if it's a valid 3 or 6 character hex
    return /^[0-9A-F]{3}$|^[0-9A-F]{6}$/i.test(hex);
};

/**
 * Normalizes a hex color to 6 characters with #
 * @param {string} color - Hex color (3 or 6 characters, with or without #)
 * @returns {string} Normalized hex color
 */
export const normalizeHexColor = (color) => {
    if (!isValidHexColor(color)) return null;

    let hex = color.replace('#', '').toUpperCase();

    // Convert 3-character hex to 6-character
    if (hex.length === 3) {
        hex = hex.split('').map(char => char + char).join('');
    }

    return `#${hex}`;
};

/**
 * Gets a contrasting color (opposite on color wheel)
 * @param {string} hexColor - Hex color string
 * @returns {string} Contrasting hex color
 */
export const getContrastingColor = (hexColor) => {
    const rgb = hexToRgb(hexColor);
    if (!rgb) return '#000000';

    const { r, g, b } = rgb;

    // Simple inversion for contrast
    return rgbToHex(255 - r, 255 - g, 255 - b);
};

/**
 * Blends two colors together
 * @param {string} color1 - First hex color
 * @param {string} color2 - Second hex color
 * @param {number} ratio - Blend ratio (0-1, 0 = all color1, 1 = all color2)
 * @returns {string} Blended hex color
 */
export const blendColors = (color1, color2, ratio = 0.5) => {
    const rgb1 = hexToRgb(color1);
    const rgb2 = hexToRgb(color2);

    if (!rgb1 || !rgb2) return color1 || color2 || '#000000';

    const blendValue = (v1, v2) => Math.round(v1 + (v2 - v1) * ratio);

    return rgbToHex(
        blendValue(rgb1.r, rgb2.r),
        blendValue(rgb1.g, rgb2.g),
        blendValue(rgb1.b, rgb2.b)
    );
};

/**
 * Generates a color palette based on a base color
 * @param {string} baseColor - Base hex color
 * @param {number} count - Number of colors to generate (default: 5)
 * @returns {Array<string>} Array of hex colors
 */
export const generateColorPalette = (baseColor, count = 5) => {
    if (!isValidHexColor(baseColor)) return [];

    const palette = [];
    const step = 100 / (count + 1);

    for (let i = 1; i <= count; i++) {
        const lightness = step * i;
        if (lightness < 50) {
            palette.push(darkenColor(baseColor, 50 - lightness));
        } else {
            palette.push(lightenColor(baseColor, lightness - 50));
        }
    }

    return palette;
};

// Export all functions as default object as well
const colorUtils = {
    hexToRgb,
    rgbToHex,
    getLuminance,
    getTextColor,
    lightenColor,
    darkenColor,
    addOpacity,
    getRandomColor,
    isValidHexColor,
    normalizeHexColor,
    getContrastingColor,
    blendColors,
    generateColorPalette
};

export default colorUtils;
